import React from 'react';
import { DefaultButton, Icon } from '@fluentui/react';
import { EntityLink } from './EntityLink';
import { StandardTable } from './StandardTable';
import styles from '../Pages/Tenants.module.css';

type AccessEntry = { portfolioId: string; communityId: string };

type TenantSummary = {
  emailNorm: string;
  firstName?: string;
  lastName?: string;
};

type EmptyState = {
  isFiltered: boolean;
  emptyMessage: string;
  filteredMessage: string;
};

interface TenantDirectoryTableProps {
  tenants: TenantSummary[];
  totalCount: number;
  loading: boolean;
  filtersSummary: string;
  onClearFilters: () => void;
  onRefresh: () => void;
  emptyState: EmptyState;
  getAccessList: (email: string) => AccessEntry[];
  getPortfolioName: (portfolioId: string) => string;
  getCommunityName: (portfolioId: string, communityId: string) => string;
  onManage: (email: string) => void;
  onDelete?: (email: string) => void;
  isDeleteDisabled?: (email: string, accessList: AccessEntry[]) => boolean;
  getDeleteTooltip?: (email: string, accessList: AccessEntry[]) => string;
  onCopy?: (value: string, label?: string) => void;
  formatName?: (value?: string) => string;
}

export const TenantDirectoryTable: React.FC<TenantDirectoryTableProps> = ({
  tenants,
  totalCount,
  loading,
  filtersSummary,
  onClearFilters,
  onRefresh,
  emptyState,
  getAccessList,
  getPortfolioName,
  getCommunityName,
  onManage,
  onDelete,
  isDeleteDisabled,
  getDeleteTooltip,
  onCopy,
  formatName,
}) => {
  const formatValue = React.useCallback(
    (value?: string) => (formatName ? formatName(value) : value || ''),
    [formatName]
  );

  return (
    <StandardTable
      items={tenants}
      totalCount={totalCount}
      loading={loading}
      filtersSummary={filtersSummary}
      onClearFilters={onClearFilters}
      onRefresh={onRefresh}
      emptyState={emptyState}
      renderTable={(paged) => (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Access</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((tenant) => {
              const displayName = [formatValue(tenant.firstName), formatValue(tenant.lastName)]
                .filter(Boolean)
                .join(' ');
              const accessList = getAccessList(tenant.emailNorm);
              const deleteTooltip = getDeleteTooltip
                ? getDeleteTooltip(tenant.emailNorm, accessList)
                : 'Delete tenant';
              const deleteDisabled = isDeleteDisabled
                ? isDeleteDisabled(tenant.emailNorm, accessList)
                : false;

              return (
                <tr key={tenant.emailNorm}>
                  <td>{displayName || 'Tenant'}</td>
                  <td className={styles.monospace}>
                    <span className={styles.inlineCopy}>
                      {tenant.emailNorm}
                      {onCopy && (
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => onCopy(tenant.emailNorm, 'Email')}
                          aria-label="Copy email"
                          title="Copy email"
                        >
                          <Icon iconName="Copy" />
                        </button>
                      )}
                    </span>
                  </td>
                  <td>
                    {accessList.length > 0 ? (
                      <div className={styles.accessList}>
                        {accessList.map((access) => {
                          const key = `${access.portfolioId}:${access.communityId}`;
                          const portfolioName = getPortfolioName(access.portfolioId);
                          const communityName = getCommunityName(
                            access.portfolioId,
                            access.communityId
                          );
                          const tooltip = `Portfolio ID: ${access.portfolioId}\nCommunity ID: ${access.communityId}`;
                          const copyValue = `${access.portfolioId}:${access.communityId}`;
                          const portfolioLink = `/portfolios/${encodeURIComponent(
                            access.portfolioId
                          )}/communities`;
                          const communityLink = `${portfolioLink}?communityId=${encodeURIComponent(
                            access.communityId
                          )}`;

                          return (
                            <span key={key} className={styles.accessChip} title={tooltip}>
                              <EntityLink
                                to={portfolioLink}
                                label={portfolioName}
                                className={styles.accessLink}
                              />
                              <span className={styles.accessDivider}>/</span>
                              <EntityLink
                                to={communityLink}
                                label={communityName}
                                className={styles.accessLink}
                              />
                              {onCopy && (
                                <button
                                  type="button"
                                  className={styles.iconButton}
                                  onClick={() => onCopy(copyValue, 'IDs')}
                                  aria-label="Copy portfolio and community IDs"
                                  title="Copy IDs"
                                >
                                  <Icon iconName="Copy" />
                                </button>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className={styles.helpText}>No access</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <DefaultButton
                        text="Manage"
                        onClick={() => onManage(tenant.emailNorm)}
                        disabled={loading}
                      />
                      {onDelete && (
                        <DefaultButton
                          text="Delete"
                          onClick={() => onDelete(tenant.emailNorm)}
                          disabled={loading || deleteDisabled}
                          title={deleteTooltip}
                          aria-label={deleteTooltip}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    />
  );
};
